import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibAddIcon } from '@chewy/kib-icons-react'
import moment from 'moment'
import { DateFormat, DateUtils } from '@pbt/pbt-ui-components'

import RhapInlineSelect from '../common/RhapInlineSelect'
import { useAgentChat } from './AgentChatContext'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './ChatSessionBar.module.scss'

const ChatSessionBar: React.FC = () => {
  const { t } = useTranslation('Agent')
  const {
    session,
    sessions,
    selectSession,
    createNewSession,
    loadSessions,
    isLoadingSessions,
  } = useAgentChat()

  // Load sessions on mount
  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  const sortedSessions = useMemo(() => {
    if (!sessions || sessions.length === 0) return []
    return [...sessions].sort((a, b) => {
      const aTime = a.lastUpdateTime || Date.now() / 1000
      const bTime = b.lastUpdateTime || Date.now() / 1000
      return bTime - aTime
    })
  }, [sessions])

  const sessionOptions = useMemo(() => {
    if (sortedSessions.length === 0) {
      return [{ value: '', label: t('NO_CHAT_HISTORY') }]
    }
    return sortedSessions.map((s) => ({
      value: s.id,
      label:
        s.state.title ||
        DateUtils.formatDate(
          moment(s.lastUpdateTime * 1000).toISOString(),
          false,
          DateFormat.FULL_DATE_TIME,
        ),
    }))
  }, [sortedSessions, t])

  const handleSessionChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    const selectedId = e.target.value
    const selectedSession = sessions.find((s) => s.id === selectedId)
    if (selectedSession) {
      selectSession(selectedSession)
    }
  }

  const handleNewChat = () => {
    createNewSession()
  }

  return (
    <div className={styles.sessionBar}>
      <div className={styles.sessionInfo}>
        <span className={styles.sessionLabel}>{t('CURRENT_CHAT')}: </span>
        <div className={styles.sessionSelectWrapper}>
          <RhapInlineSelect
            hiddenLabel
            className={styles.sessionSelect}
            disabled={isLoadingSessions || sortedSessions.length === 0}
            id="chat-session-select"
            label={t('CURRENT_SESSION')}
            options={sessionOptions}
            value={session?.id ?? ''}
            onChange={handleSessionChange}
          />
        </div>
      </div>
      <KibButtonNew
        aria-label={t('NEW_CHAT')}
        className={styles.newChatButton}
        emphasis="tertiary"
        icon={<KibAddIcon />}
        iconPosition="start"
        size="small"
        theme="utility"
        title={t('NEW_CHAT')}
        onClick={handleNewChat}
      >
        {t('NEW_CHAT')}
      </KibButtonNew>
    </div>
  )
}

export default ChatSessionBar
